import { fingerprintArticleValues } from 'Helpers/Blog/blogForm'
import {
  buildSnapshot,
  clearConflictBackup,
  clearRecoverySnapshot,
  listUnsavedDraftSnapshots,
  readConflictBackup,
  readRecoverySnapshot,
  saveConflictBackup,
  saveRecoverySnapshot,
} from 'Helpers/Blog/blogRecovery'
import { ArticleFormValues, BlogRecoverySnapshot } from 'Models'
import { useCallback, useMemo, useRef, useState } from 'react'

/**
 * Owns the localStorage recovery snapshot for one editor session. The
 * backend stays the source of truth; this is only a safety net.
 *
 * `slug` is the article's CURRENT slug (the route slug, or the slug returned
 * by the first successful create). While it is null the article has never
 * been saved, and a per-mount temporary id is used as the storage key.
 */
export const useBlogRecovery = (slug: string | null) => {
  const tempIdRef = useRef<string>(`new-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`)
  /** Storage key of an older never-saved draft that was offered for recovery. Retired once restored or discarded, so it can't be offered again after it has been saved as a real article. */
  const candidateKeyRef = useRef<string | null>(null)
  const identifier = slug ?? tempIdRef.current

  const [recoverableSnapshot, setRecoverableSnapshot] = useState<BlogRecoverySnapshot | null>(null)
  const [candidateDrafts] = useState(() => (slug === null ? listUnsavedDraftSnapshots() : []))

  /**
   * Looks for unsaved local work worth offering to the admin. A snapshot that
   * is identical to what the server already has is discarded rather than offered.
   *
   * @param serverValues Form values built from the just-fetched article (null for a new article)
   */
  const checkForRecovery = useCallback(
    (serverValues: ArticleFormValues | null) => {
      let candidate: BlogRecoverySnapshot | null = null

      if (slug) {
        candidate = readConflictBackup(slug) ?? readRecoverySnapshot(slug)
      } else if (candidateDrafts.length > 0) {
        candidateKeyRef.current = candidateDrafts[0].key
        candidate = readRecoverySnapshot(candidateDrafts[0].key)
      }

      if (!candidate) return

      if (
        serverValues &&
        fingerprintArticleValues(candidate.values) === fingerprintArticleValues(serverValues)
      ) {
        if (slug) {
          clearRecoverySnapshot(slug)
          clearConflictBackup(slug)
        }
        return
      }
      setRecoverableSnapshot(candidate)
    },
    [slug, candidateDrafts],
  )

  const persistSnapshot = useCallback(
    (baseVersion: number | null, changeCounter: number, values: ArticleFormValues) => {
      saveRecoverySnapshot(identifier, buildSnapshot(slug, baseVersion, changeCounter, values))
    },
    [identifier, slug],
  )

  /** Copies the admin's current work to the conflict backup key (409). Survives further edits. */
  const preserveForConflict = useCallback(
    (baseVersion: number | null, changeCounter: number, values: ArticleFormValues) => {
      if (slug) saveConflictBackup(slug, buildSnapshot(slug, baseVersion, changeCounter, values))
    },
    [slug],
  )

  /** Clears the live snapshot after a CONFIRMED save. Leaves any conflict backup alone. */
  const clear = useCallback(() => {
    clearRecoverySnapshot(identifier)
    clearRecoverySnapshot(tempIdRef.current) // the pre-create temp key, once a slug exists
    setRecoverableSnapshot(null)
  }, [identifier])

  /** Explicit user discard: removes the live snapshot AND the conflict backup. */
  const discard = useCallback(() => {
    clear()
    if (slug) clearConflictBackup(slug)
    if (candidateKeyRef.current) clearRecoverySnapshot(candidateKeyRef.current)
  }, [clear, slug])

  /** Hands the snapshot to the caller to load into the form, and retires the conflict backup (its content is now in the form). */
  const takeSnapshot = useCallback((): BlogRecoverySnapshot | null => {
    const snapshot = recoverableSnapshot
    if (slug) clearConflictBackup(slug)
    // The restored content is about to be re-persisted under the live key by the
    // change tracker, so the old never-saved copy can go.
    if (candidateKeyRef.current) clearRecoverySnapshot(candidateKeyRef.current)
    setRecoverableSnapshot(null)
    return snapshot
  }, [recoverableSnapshot, slug])

  const dismissPrompt = useCallback(() => setRecoverableSnapshot(null), [])

  return useMemo(
    () => ({
      recoverableSnapshot,
      checkForRecovery,
      persistSnapshot,
      preserveForConflict,
      clear,
      discard,
      takeSnapshot,
      dismissPrompt,
    }),
    [
      recoverableSnapshot,
      checkForRecovery,
      persistSnapshot,
      preserveForConflict,
      clear,
      discard,
      takeSnapshot,
      dismissPrompt,
    ],
  )
}
